import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, ready, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && !user) {
    navigate("/login");
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("Le password non coincidono.");
      return;
    }
    if (next.length < 8) {
      setError("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata con successo");
      await refresh();
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy p-6">
      <div className="w-full max-w-md bg-white rounded-lg border border-navy/10 p-8 shadow-xl">
        <div className="w-12 h-12 rounded-md bg-gold/15 flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-gold" />
        </div>
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Primo accesso</p>
        <h1 className="font-display text-2xl font-bold text-navy mt-2">Imposta una nuova password</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Per motivi di sicurezza, al primo accesso è necessario cambiare la password provvisoria.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current">Password attuale</Label>
            <Input id="current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
              className="h-11" data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next">Nuova password</Label>
            <Input id="next" type="password" required value={next} onChange={(e) => setNext(e.target.value)}
              className="h-11" data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Conferma nuova password</Label>
            <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="h-11" data-testid="confirm-password-input" />
          </div>
          {error && <p className="text-sm text-destructive font-medium" data-testid="change-password-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="change-password-submit"
            className="w-full h-11 bg-navy hover:bg-navy-hover text-white font-semibold">
            {loading ? "Salvataggio…" : "Salva e continua"}
          </Button>
          <button type="button" onClick={() => { logout(); navigate("/login"); }}
            className="w-full text-sm text-muted-foreground hover:text-navy transition-colors" data-testid="change-password-logout">
            Esci
          </button>
        </form>
      </div>
    </div>
  );
}
